<template>
  <div class="supplier-reviews" :class="`supplier-reviews--${mode}`">
    <!-- ========== COMPACT MODE (for list view) ========== -->
    <template v-if="mode === 'compact'">
      <div class="reviews-compact">
        <!-- Rating Stars -->
        <div v-if="reviewCount > 0" class="reviews-compact__rating">
          <span class="reviews-compact__stars">
            <span 
              v-for="n in 5" 
              :key="n" 
              class="star"
              :class="{ 'star--filled': n <= Math.round(averageRating) }"
            >
              ★
            </span>
          </span>
          <span class="reviews-compact__number">{{ averageRating.toFixed(1) }}</span>
          <span class="reviews-compact__count">({{ reviewCount }})</span>
        </div>
        <div v-else class="reviews-compact__empty">
          <span class="reviews-compact__stars">
            <span v-for="n in 5" :key="n" class="star">★</span>
          </span>
          <span class="reviews-compact__count">No reviews yet</span>
        </div>
        
        <!-- Write Review Button (compact) -->
        <button 
          v-if="showReviewButton && isLoggedIn && canReview && showWriteButton" 
          type="button" 
          class="reviews-compact__btn"
          @click="openReviewForm"
        >
          <i class="fas fa-pen"></i> Write Review
        </button>
      </div>

      <!-- Review Form for Compact Mode — floats as a popover so opening
           it never reflows whatever row/list it's embedded in. -->
      <transition name="slide-down">
        <div v-if="showForm" class="review-form review-form--compact">
          <div class="review-form__header">
            <h5>Write a Review</h5>
            <button type="button" class="review-form__close" @click="closeReviewForm">✕</button>
          </div>
          
          <form @submit.prevent="submitReview">
            <div class="review-form__group">
              <label class="review-form__label">Rating</label>
              <div class="review-form__stars">
                <button
                  v-for="n in 5"
                  :key="n"
                  type="button"
                  class="review-form__star"
                  :class="{ 'review-form__star--active': n <= (hoverStar || form.stars) }"
                  @click="form.stars = n"
                  @mouseenter="hoverStar = n"
                  @mouseleave="hoverStar = 0"
                >
                  ★
                </button>
              </div>
              <span v-if="formErrors.stars" class="review-form__error">{{ formErrors.stars }}</span>
            </div>

            <div class="review-form__group">
              <label class="review-form__label" for="review-compact">Review</label>
              <textarea
                id="review-compact"
                v-model="form.review"
                class="review-form__textarea"
                rows="3"
                placeholder="Share your experience with this supplier..."
                maxlength="2000"
              ></textarea>
              <div class="review-form__char-count">{{ form.review.length }}/2000</div>
              <span v-if="formErrors.review" class="review-form__error">{{ formErrors.review }}</span>
            </div>

            <div class="review-form__actions">
              <button type="button" class="review-form__btn review-form__btn--cancel" @click="closeReviewForm">
                Cancel
              </button>
              <button type="submit" class="review-form__btn review-form__btn--submit" :disabled="submitting">
                {{ submitting ? 'Submitting...' : 'Submit Review' }}
              </button>
            </div>
          </form>
        </div>
      </transition>

      <!-- Backdrop: click-away to close, only rendered while the popover is open -->
      <div v-if="showForm" class="review-form__backdrop" @click="closeReviewForm"></div>
    </template>

    <!-- ========== FULL MODE (for profile page) ========== -->
    <template v-else>
      <!-- Reviews Header -->
      <div class="reviews-header">
        <div class="reviews-header__left">
          <span class="reviews-header__icon">⭐</span>
          <div>
            <h4 class="reviews-header__title">Reviews & Ratings</h4>
            <p class="reviews-header__desc">
              {{ reviewCount }} review{{ reviewCount !== 1 ? 's' : '' }} · 
              {{ averageRating > 0 ? averageRating.toFixed(1) : 'No' }} ★ average
            </p>
          </div>
        </div>
        
        <!-- Write Review Button -->
        <button 
          v-if="showReviewButton && isLoggedIn && canReview" 
          type="button" 
          class="reviews-header__btn"
          @click="openReviewForm"
        >
          <i class="fas fa-pen"></i>
          Write a Review
        </button>
      </div>

      <!-- Average Rating Display -->
      <div v-if="reviewCount > 0" class="reviews-summary">
        <div class="reviews-summary__rating">
          <span class="reviews-summary__stars">
            <span 
              v-for="n in 5" 
              :key="n" 
              class="star"
              :class="{ 'star--filled': n <= Math.round(averageRating) }"
            >
              ★
            </span>
          </span>
          <span class="reviews-summary__number">{{ averageRating.toFixed(1) }}</span>
        </div>
        <div class="reviews-summary__count">
          {{ reviewCount }} review{{ reviewCount !== 1 ? 's' : '' }}
        </div>
      </div>

      <!-- Review Form for Full Mode -->
      <transition name="slide-down">
        <div v-if="showForm" class="review-form">
          <div class="review-form__header">
            <h5>Write a Review</h5>
            <button type="button" class="review-form__close" @click="closeReviewForm">✕</button>
          </div>
          
          <form @submit.prevent="submitReview">
            <div class="review-form__group">
              <label class="review-form__label">Rating</label>
              <div class="review-form__stars">
                <button
                  v-for="n in 5"
                  :key="n"
                  type="button"
                  class="review-form__star"
                  :class="{ 'review-form__star--active': n <= (hoverStar || form.stars) }"
                  @click="form.stars = n"
                  @mouseenter="hoverStar = n"
                  @mouseleave="hoverStar = 0"
                >
                  ★
                </button>
              </div>
              <span v-if="formErrors.stars" class="review-form__error">{{ formErrors.stars }}</span>
            </div>

            <div class="review-form__group">
              <label class="review-form__label" for="review-full">Review</label>
              <textarea
                id="review-full"
                v-model="form.review"
                class="review-form__textarea"
                rows="4"
                placeholder="Share your experience with this supplier..."
                maxlength="2000"
              ></textarea>
              <div class="review-form__char-count">{{ form.review.length }}/2000</div>
              <span v-if="formErrors.review" class="review-form__error">{{ formErrors.review }}</span>
            </div>

            <div class="review-form__actions">
              <button type="button" class="review-form__btn review-form__btn--cancel" @click="closeReviewForm">
                Cancel
              </button>
              <button type="submit" class="review-form__btn review-form__btn--submit" :disabled="submitting">
                {{ submitting ? 'Submitting...' : 'Submit Review' }}
              </button>
            </div>
          </form>
        </div>
      </transition>

      <!-- Reviews List -->
      <div v-if="reviews.length > 0" class="reviews-list">
        <div 
          v-for="review in reviews" 
          :key="review.id" 
          class="review-item"
        >
          <div class="review-item__header">
            <div class="review-item__identity">
              <div class="review-item__avatar">
                 <img v-if="review.reviewer?.photo" :src="getFileUrl(review.reviewer.photo)" alt="" />
              </div>
              <div>
                <strong class="review-item__name">{{ getReviewerName(review.reviewer) }}</strong>
                <span class="review-item__date">{{ formatDate(review.created_at) }}</span>
              </div>
            </div>
            <div class="review-item__stars">
              <span 
                v-for="n in 5" 
                :key="n" 
                class="star"
                :class="{ 'star--filled': n <= review.stars }"
              >
                ★
              </span>
            </div>
          </div>
          
          <p v-if="review.review" class="review-item__content">{{ review.review }}</p>
          <p v-if="review.comments" class="review-item__comments">{{ review.comments }}</p>
          
          <div v-if="review.status == 'reported'" class="review-item__status review-item__status--pending">
            🚩 Reported
          </div>

          <!-- Report abuse -->
          <div v-if="isLoggedIn && mySupplierId && Number(mySupplierId) === Number(supplierId)" class="review-item__report">
            <button
              v-if="review.status !== 'reported'"
              type="button"
              class="review-item__report-btn"
              @click="toggleReport(review)"
            >
              <i class="fas fa-flag"></i> Report abuse
            </button>
            <span v-else class="review-item__reported-badge">🚩 Reported</span>

            <div v-if="reportingId === review.id" class="report-panel">
              <label class="review-form__label">Why are you reporting this review?</label>
              <textarea
                v-model="reportReason"
                class="review-form__textarea"
                rows="3"
                placeholder="Explain what's wrong with this review..."
                maxlength="1000"
              ></textarea>

              <label class="report-panel__file-label">
                <i class="fas fa-paperclip"></i> Attach evidence (optional)
                <input type="file" multiple class="report-panel__file-input" @change="onReportFilesSelected" />
              </label>
              <div v-if="reportFiles.length" class="report-panel__files">
                <span v-for="(f, i) in reportFiles" :key="i" class="report-panel__file-chip">{{ f.name }}</span>
              </div>
              <span v-if="reportError" class="review-form__error">{{ reportError }}</span>

              <div class="review-form__actions">
                <button type="button" class="review-form__btn review-form__btn--cancel" @click="reportingId = null">
                  Cancel
                </button>
                <button
                  type="button"
                  class="review-form__btn review-form__btn--submit"
                  :disabled="reportSubmitting"
                  @click="submitReport(review)"
                >
                  {{ reportSubmitting ? 'Submitting...' : 'Submit Report' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading" class="reviews-empty">
        <span class="reviews-empty__icon">📝</span>
        <p class="reviews-empty__text">No reviews yet</p>
        <p class="reviews-empty__sub" v-if="showReviewButton">Be the first to review this supplier</p>
        <p class="reviews-empty__sub" v-else>No reviews available</p>
      </div>

      <!-- Loading & Error States -->
      <div v-if="loading" class="reviews-loading">
        <span class="reviews-loading__spinner"></span>
        Loading reviews...
      </div>

      <div v-if="error" class="reviews-error">
        <span class="reviews-error__icon">⚠️</span>
        <p>{{ error }}</p>
        <button type="button" class="reviews-error__retry" @click="loadReviews">Try Again</button>
      </div>
    </template>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
export default {
  name: 'SupplierReviews',
  props: {
    supplierId: {
      type: Number,
      required: true
    },
    supplierName: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'full', // 'full' | 'compact'
      validator: (value) => ['full', 'compact'].includes(value)
    },
    showWriteButton: {
      type: Boolean,
      default: true
    },
    showReviewButton: {
      type: Boolean,
      default: true
    },
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      reviews: [],
      averageRating: 0,
      reviewCount: 0,
      loading: false,
      error: '',
      showForm: false,
      submitting: false,
      hoverStar: 0,
      form: {
        stars: 0,
        review: '',
        comments: ''
      },
      formErrors: {},
      isLoggedIn: !!localStorage.getItem('supplier_id'),
      mySupplierId: localStorage.getItem('supplier_id'),
      hasReviewed: false,

      // ---- Report abuse ----
      reportingId: null,
      reportReason: '',
      reportFiles: [],
      reportSubmitting: false,
      reportError: '',
    }
  },
  computed: {
    canReview() {
      return this.isLoggedIn && !this.hasReviewed
    }
  },
  mounted() {
    if (this.autoLoad) {
      this.loadReviews()
    }
  },
  methods: {
     getFileUrl(filePath) {
      return resolveAssetFileUrl(filePath)
    },
    async loadReviews() {
      if (!this.supplierId) return
      
      this.loading = true
      this.error = ''

      const result = await form_api_submit({
        url: `supplier/${this.supplierId}/reviews`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: false
      })

      this.loading = false

      if (result.success) {
        const data = result.data
        this.reviews = data.reviews || []
        console.log('Loaded reviews:', this.reviews)
        this.averageRating = data.average_rating || 0
        this.reviewCount = data.review_count || 0
        
        if (this.isLoggedIn) {
          this.hasReviewed = this.reviews.some(
            review => review.reviewed_by === parseInt(this.mySupplierId)
          )
        }
      } else {
        this.error = 'Failed to load reviews. Please try again.'
      }
    },

    openReviewForm() {
      if (!this.isLoggedIn) {
        this.$emit('login-required')
        return
      }
      this.showForm = true
      this.formErrors = {}
    },

    closeReviewForm() {
      this.showForm = false
      this.form = { stars: 0, review: '', comments: '' }
      this.formErrors = {}
      this.hoverStar = 0
    },

    async submitReview() {
      this.submitting = true
      this.formErrors = {}

      const result = await form_api_submit({
        url: 'reviews',
        method: 'POST',
        data: {
          supplier_id: this.supplierId,
          stars: this.form.stars,
          review: this.form.review,
          comments: this.form.comments
        },
        show_success_toast: true,
        show_error_toast: true
      })

      this.submitting = false

      if (result.success) {
        this.closeReviewForm()
        this.hasReviewed = true
        await this.loadReviews()
        this.$emit('review-submitted')
      } else {
        if (result.data?.errors) {
          this.formErrors = result.data.errors
        } else {
          this.error = result.message || 'Failed to submit review. Please try again.'
        }
      }
    },

    getInitials(reviewer) {
      if (!reviewer) return '?'
      const name = reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim()
      return name
        .split(' ')
        .map(w => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    getReviewerName(reviewer) {
      if (!reviewer) return 'Anonymous'
      return reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() || 'Anonymous'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    refresh() {
      this.loadReviews()
    },

    // ---- Report abuse ----
    toggleReport(review) {
      if (!this.isLoggedIn) {
        this.$emit('login-required')
        return
      }
      this.reportingId = this.reportingId === review.id ? null : review.id
      this.reportReason = ''
      this.reportFiles = []
      this.reportError = ''
    },

    onReportFilesSelected(e) {
      this.reportFiles.push(...Array.from(e.target.files))
      e.target.value = ''
    },

    async submitReport(review) {
      if (!this.reportReason.trim()) {
        this.reportError = 'Please explain why you\'re reporting this review.'
        return
      }

      this.reportSubmitting = true
      this.reportError = ''

      const fd = new FormData()
      fd.append('report_reason', this.reportReason.trim())
      this.reportFiles.forEach((f) => fd.append('attachments[]', f))

      const result = await form_api_submit({
        url: `reviews/${review.id}/report-abuse`,
        method: 'POST',
        data: fd,
        show_success_toast: true,
        show_error_toast: true,
      })

      this.reportSubmitting = false

      if (result.success) {
        review.status = 'reported'
        this.reportingId = null
      } else {
        this.reportError = result?.message || 'Could not submit the report. Please try again.'
      }
    }
  }
}
</script>

<style scoped>
.star {
  display: inline-block;
  color: #d1d5db;
}

.star--filled {
  color: #f59e0b;
}

/* ========== COMPACT MODE ========== */
.supplier-reviews--compact {
  display: inline-block;
  position: relative;
}

.reviews-compact {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: nowrap;
}

.reviews-compact__rating {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.reviews-compact__stars {
  display: flex;
  gap: 1px;
  font-size: 14px;
}

.reviews-compact__number {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
  margin-left: 0.25rem;
}

.reviews-compact__count {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-left: 0.25rem;
}

.reviews-compact__empty {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.reviews-compact__empty .reviews-compact__stars {
  font-size: 14px;
  opacity: 0.5;
}

.reviews-compact__empty .reviews-compact__count {
  font-size: 12px;
  color: var(--color-text-muted);
  font-style: italic;
}

.reviews-compact__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-dark);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.reviews-compact__btn:hover {
  background: var(--color-tint-light);
  border-color: var(--color-secondary);
  transform: translateY(-1px);
}

.reviews-compact__btn:active {
  transform: scale(0.96);
}

.reviews-compact__btn i {
  font-size: 11px;
}
.review-form--compact {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(520px, calc(100vw - 32px));
  z-index: 60;
  margin-top: 0;
  padding: 1.25rem; /* Slightly more padding */
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 16px 40px -12px rgba(0, 0, 0, 0.22);
  max-height: min(90vh, 500px);
  overflow-y: auto; 
}

.review-form__backdrop {
  position: fixed;
  inset: 0;
  z-index: 55;
  background: transparent;
}

.review-form--compact .review-form__header h5 {
  font-size: 14px;
}

.review-form--compact .review-form__star {
  font-size: 24px;
}

.review-form--compact .review-form__textarea {
  font-size: 12px;
  padding: 0.5rem;
}

.review-form--compact .review-form__btn {
  padding: 0.4rem 1rem;
  font-size: 12px;
}

/* ========== FULL MODE ========== */
.supplier-reviews--full {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
}

.reviews-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.reviews-header__left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.reviews-header__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: var(--color-tint-light);
  display: grid;
  place-items: center;
  font-size: 14px;
  border-radius: 6px;
}

.reviews-header__title {
  margin: 0 0 1px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.reviews-header__desc {
  margin: 0;
  font-size: 11.5px;
  color: var(--color-text-muted);
}

.reviews-header__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
}

.reviews-header__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px -6px rgba(12, 18, 83, 0.4);
}

.reviews-header__btn:active {
  transform: scale(0.96);
}

.reviews-summary {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 0;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.reviews-summary__rating {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.reviews-summary__stars {
  display: flex;
  gap: 2px;
  font-size: 20px;
  color: #d1d5db;
}

.reviews-summary__stars .star--filled {
  color: #f59e0b;
}

.reviews-summary__number {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.reviews-summary__count {
  font-size: 14px;
  color: var(--color-text-muted);
}

/* Review Form */
.review-form {
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.review-form__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.review-form__header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.review-form__close {
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 0.25rem 0.5rem;
  transition: color 0.2s;
}

.review-form__close:hover {
  color: var(--color-text-dark);
}

.review-form__group {
  margin-bottom: 1.25rem;
}

.review-form__group:last-child {
  margin-bottom: 0;
}

.review-form__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
  margin-bottom: 0.5rem;
}

.review-form__stars {
  display: flex;
  gap: 0.5rem;
}

.review-form__star {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #d1d5db;
  transition: color 0.2s, transform 0.1s;
  padding: 0;
  line-height: 1;
}

.review-form__star:hover {
  transform: scale(1.1);
}

.review-form__star--active {
  color: #f59e0b;
}

.review-form__textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  color: var(--color-text-dark);
  resize: vertical;
  transition: border-color 0.2s;
  background: var(--color-surface);
}

.review-form__textarea:focus {
  outline: none;
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(11, 23, 153, 0.08);
}

.review-form__char-count {
  font-size: 11px;
  color: var(--color-text-muted);
  text-align: right;
  margin-top: 0.25rem;
}

.review-form__error {
  display: block;
  font-size: 12px;
  color: #b3261e;
  margin-top: 0.25rem;
}

.review-form__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
}

.review-form__btn {
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.review-form__btn--cancel {
  background: var(--color-surface);
  color: var(--color-text-dark);
  border: 1px solid var(--color-border);
}

.review-form__btn--cancel:hover {
  background: var(--color-tint-light);
}

.review-form__btn--submit {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
}

.review-form__btn--submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px -6px rgba(12, 18, 83, 0.4);
}

.review-form__btn--submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Reviews List */
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.review-item {
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  transition: box-shadow 0.2s;
}

.review-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.review-item__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.review-item__identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.review-item__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.review-item__name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.review-item__date {
  display: block;
  font-size: 11px;
  color: var(--color-text-muted);
}

.review-item__stars {
  display: flex;
  gap: 2px;
  font-size: 16px;
  color: #d1d5db;
  flex-shrink: 0;
}

.review-item__stars .star--filled {
  color: #f59e0b;
}

.review-item__content {
  margin: 0 0 0.5rem;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-dark);
}

.review-item__comments {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-muted);
  font-style: italic;
}

.review-item__status {
  display: inline-block;
  margin-top: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.review-item__status--pending {
  background: #fef3c7;
  color: #92400e;
}

/* Report abuse */
.review-item__report {
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--color-border);
}

.review-item__report-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;
  transition: color 0.15s ease;
}

.review-item__report-btn:hover {
  color: #b3261e;
}

.review-item__reported-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 11px;
  font-weight: 700;
  color: #b3261e;
}

.report-panel {
  margin-top: 0.75rem;
  padding: 0.875rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
}

.report-panel .review-form__label {
  color: #7f1d1d;
}

.report-panel .review-form__textarea {
  border-color: #fca5a5;
}

.report-panel__file-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  color: #7f1d1d;
  cursor: pointer;
}

.report-panel__file-input {
  display: none;
}

.report-panel__files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.5rem;
}

.report-panel__file-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #fecaca;
  color: #7f1d1d;
}

.report-panel .review-form__btn--submit {
  background: linear-gradient(145deg, #b3261e, #7f1d1d);
}

/* Empty State */
.reviews-empty {
  text-align: center;
  padding: 2rem 1rem;
}

.reviews-empty__icon {
  display: block;
  font-size: 48px;
  margin-bottom: 0.75rem;
}

.reviews-empty__text {
  margin: 0 0 0.25rem;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.reviews-empty__sub {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted);
}

/* Loading State */
.reviews-loading {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--color-text-muted);
  font-size: 13px;
}

.reviews-loading__spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.75rem;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error State */
.reviews-error {
  text-align: center;
  padding: 1.5rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.reviews-error__icon {
  font-size: 24px;
  display: block;
  margin-bottom: 0.5rem;
}

.reviews-error p {
  margin: 0 0 0.75rem;
  color: #b3261e;
  font-size: 13px;
}

.reviews-error__retry {
  padding: 0.5rem 1.25rem;
  border: 1px solid #b3261e;
  background: transparent;
  color: #b3261e;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.reviews-error__retry:hover {
  background: #b3261e;
  color: #fff;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 640px) {
  .supplier-reviews--full .reviews-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .reviews-header__btn {
    justify-content: center;
  }

  .review-item__header {
    flex-direction: column;
    align-items: stretch;
  }

  .review-item__stars {
    align-self: flex-start;
  }

  .reviews-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .review-form__actions {
    flex-direction: column;
  }

  .review-form__btn {
    width: 100%;
    justify-content: center;
  }

  .reviews-compact {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .review-form--compact {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    top: calc(100% + 8px);
  }
}
</style>